import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useDrawerEditor } from '@/features/sessions/components/workspace/DetailDrawer/SessionPanel/DrawerEditorContext';
import { cn } from '@/lib/utils';

import { BubbleToolbar } from './BubbleToolbar';
import { EditorDocumentHeader } from './EditorDocumentHeader';
import { EditorToolbar } from './EditorToolbar';
import { ShortcutHintOverlay } from './ShortcutHintOverlay';
import { buildEditorExtensions } from './extensions';

export type EditorKind = 'note' | 'case-report';
export type AutosaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'failed';

export interface EditorDocument {
  id: string;
  content: string | null;
  status: string;
  updatedAt?: string | null;
}

export interface DocumentMeta {
  clientName: string;
  scheduledStart: string | null;
  authorLabel: string;
}

interface Props {
  kind: EditorKind;
  document: EditorDocument | null;
  isReadOnly: boolean;
  onAutosave: (content: string) => Promise<void>;
  topbar: (ctx: ShellContext) => ReactNode;
  footer: (ctx: ShellContext) => ReactNode;
  sidePanel: ReactNode;
  /** Drives the in-document editorial header (title + meta line). */
  documentMeta: DocumentMeta;
}

export interface ShellContext {
  editor: Editor | null;
  autosaveState: AutosaveState;
  wordCount: number;
  characterCount: number;
  focusMode: boolean;
  toggleFocusMode: () => void;
  forceSave: () => void;
}

const FOCUS_MODE_KEY = 'editor.focusMode';
const AUTOSAVE_DEBOUNCE_MS = 2000;
const SAVED_LINGER_MS = 5000;

export function ClinicalEditorShell({
  kind,
  document,
  isReadOnly,
  onAutosave,
  topbar,
  footer,
  sidePanel,
  documentMeta,
}: Props) {
  const drawerEditor = useDrawerEditor();
  const inDrawer = drawerEditor !== null;
  const [autosaveState, setAutosaveState] = useState<AutosaveState>('idle');
  const [focusMode, setFocusMode] = useState<boolean>(() => {
    return globalThis.localStorage.getItem(FOCUS_MODE_KEY) === 'true';
  });
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedLingerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<(() => void) | null>(null);
  const onAutosaveRef = useRef(onAutosave);
  const documentRef = useRef(document);
  const shownDocumentIdRef = useRef(document?.id);
  const editedRef = useRef(false);
  useEffect(() => {
    onAutosaveRef.current = onAutosave;
    documentRef.current = document;
  });

  const flushPendingSave = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const save = pendingSaveRef.current;
    pendingSaveRef.current = null;
    save?.();
  }, []);

  const placeholder = 'Start writing — type / for commands';
  const extensions = useMemo(() => buildEditorExtensions(placeholder, kind), [kind]);

  // `immediatelyRender: false` keeps the hook returning `Editor | null` so the
  // shell context can pass through a stable nullable type to slot consumers.
  const editor = useEditor(
    {
      extensions,
      content: document?.content ?? '',
      editable: !isReadOnly,
      immediatelyRender: false,
      onUpdate: ({ editor: ed, transaction }) => {
        // Content swapped in for a viewer is not an edit; never save it. Nor is
        // a plugin-only change, like TrailingNode padding an AI draft on load.
        if (isReadOnly || !transaction.docChanged) return;
        editedRef.current = true;
        setAutosaveState('dirty');
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const save = (): void => {
          const html = ed.getHTML();
          setAutosaveState('saving');
          onAutosaveRef
            .current(html)
            .then(() => {
              setAutosaveState('saved');
              if (savedLingerRef.current) clearTimeout(savedLingerRef.current);
              savedLingerRef.current = setTimeout(() => {
                setAutosaveState('idle');
              }, SAVED_LINGER_MS);
            })
            .catch(() => {
              setAutosaveState('failed');
              // Still unsaved: retry on blur or leave, and keep the leave warning.
              pendingSaveRef.current ??= save;
            });
        };
        pendingSaveRef.current = save;
        debounceRef.current = setTimeout(flushPendingSave, AUTOSAVE_DEBOUNCE_MS);
      },
      // Sends the pending edit before Seal. An unsaved (null) document waits for the timer.
      onBlur: () => {
        if (documentRef.current !== null) flushPendingSave();
      },
    },
    // Not the document id: each document mounts its own shell, and a new note
    // getting its id on first save must keep this editor, its cursor and typing.
    [extensions, isReadOnly],
  );

  // A document that lands on an untouched editor (an AI draft arriving while a
  // blank new note is open) is shown. Once the user has typed, their text stays.
  const documentId = document?.id;
  useEffect(() => {
    const arrived = documentRef.current;
    if (!editor || arrived === null || arrived.id === shownDocumentIdRef.current) return;
    shownDocumentIdRef.current = arrived.id;
    if (!editedRef.current)
      editor.commands.setContent(arrived.content ?? '', { emitUpdate: false });
  }, [editor, documentId]);

  useEffect(() => {
    const warnUnsaved = (event: BeforeUnloadEvent) => {
      if (pendingSaveRef.current !== null) event.preventDefault();
    };
    window.addEventListener('beforeunload', warnUnsaved);
    return () => {
      window.removeEventListener('beforeunload', warnUnsaved);
      if (savedLingerRef.current) clearTimeout(savedLingerRef.current);
    };
  }, []);

  // Save the pending edit to its own document on unmount. A document with no id
  // may still be creating, so its edit is dropped, not sent twice.
  useEffect(() => {
    return () => {
      if (documentRef.current === null) pendingSaveRef.current = null;
      flushPendingSave();
    };
  }, [flushPendingSave]);

  useEffect(() => {
    globalThis.localStorage.setItem(FOCUS_MODE_KEY, focusMode ? 'true' : 'false');
  }, [focusMode]);

  const toggleFocusMode = useCallback(() => {
    setFocusMode((prev) => !prev);
  }, []);

  const forceSave = useCallback(() => {
    if (!editor || isReadOnly) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    pendingSaveRef.current = null;
    const html = editor.getHTML();
    setAutosaveState('saving');
    void onAutosaveRef
      .current(html)
      .then(() => {
        setAutosaveState('saved');
      })
      .catch(() => {
        setAutosaveState('failed');
      });
  }, [editor, isReadOnly]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isInTextField =
        e.target instanceof HTMLElement &&
        (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA');
      const inEditor = e.target instanceof HTMLElement && e.target.closest('.ProseMirror') !== null;

      // ⌘+S force save
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        forceSave();
        return;
      }
      // ⌘+. toggle focus mode
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault();
        toggleFocusMode();
        return;
      }
      // ? overlay (only when not in a text field / editor)
      if (e.key === '?' && !isInTextField && !inEditor && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [forceSave, toggleFocusMode]);

  const wordCount = editor?.storage.characterCount.words() ?? 0;
  const characterCount = editor?.storage.characterCount.characters() ?? 0;

  const ctx: ShellContext = {
    editor,
    autosaveState,
    wordCount,
    characterCount,
    focusMode,
    toggleFocusMode,
    forceSave,
  };

  // react-hooks/refs (v7) infers that `ctx.editor` is ref-backed and flags
  // passing it through a render-time function call as a ref-read-during-render.
  // This is a known false positive (facebook/react#35575): `editor` is not a
  // raw ref but an object returned by useEditor that the rule over-infers.
  // The calls are safe: topbar/footer are pure render functions; editor is not
  // a DOM ref being read for layout. Suppress at both call sites.
  return (
    // Outer canvas. In the drawer the parent Sheet already owns the
    // elevation, so we go flush — no tinted page, no card frame. On a
    // standalone route the editor sits on a soft brand-tinted page and
    // gets a restrained card frame: subtle radius, hairline border, no
    // dramatic shadow.
    <div className={cn('flex h-full flex-col', inDrawer ? 'bg-white' : 'bg-background p-4')}>
      <div
        // overflow-hidden clips topbar/footer/sticky chrome; flex-col +
        // min-h-0 keeps the inner scroll container sized correctly inside
        // the frame regardless of mode.
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-hidden bg-white',
          inDrawer
            ? ''
            : 'border-accent-ink/10 rounded-lg border shadow-[0_2px_8px_-4px_rgba(3,68,92,0.06)]',
        )}
      >
        {/* eslint-disable-next-line react-hooks/refs */}
        {topbar(ctx)}
        {/* The persistent toolbar stays visible in focus mode — the side rail
            disappears, but formatting controls are still useful while writing.
            Only hides when the editor is read-only (sealed/submitted). */}
        {!isReadOnly ? <EditorToolbar editor={editor} kind={kind} /> : null}
        <div className="flex flex-1 overflow-hidden">
          <main
            className={cn(
              // The prose viewport is its own page-tinted column. The thin border
              // on the right separates the writing surface from the side rail
              // without dropping a heavy shadow.
              'flex flex-1 flex-col overflow-y-auto bg-white transition-all motion-reduce:transition-none',
            )}
          >
            <div
              className={cn(
                'mx-auto w-full px-8 pt-10 pb-24 transition-all motion-reduce:transition-none',
                focusMode ? 'max-w-190' : 'max-w-180',
              )}
            >
              <EditorDocumentHeader
                kind={kind}
                clientName={documentMeta.clientName}
                scheduledStart={documentMeta.scheduledStart}
                authorLabel={documentMeta.authorLabel}
                autosaveState={autosaveState}
                wordCount={wordCount}
              />
              {!isReadOnly ? <BubbleToolbar editor={editor} /> : null}
              <EditorContent
                editor={editor}
                className={cn(
                  'prose-editorial-doc focus-within:outline-none',
                  isReadOnly && 'text-neutral-700',
                )}
              />
            </div>
          </main>
          {focusMode ? null : (
            <aside className="border-accent-ink/10 flex w-90 shrink-0 flex-col gap-3 overflow-y-auto border-l bg-neutral-50/40 p-5">
              {sidePanel}
            </aside>
          )}
        </div>
        {/* eslint-disable-next-line react-hooks/refs */}
        {footer(ctx)}
      </div>
      <ShortcutHintOverlay open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  );
}
